import React, { useState } from 'react';
import SEO from '../components/SEO';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { companyInfo, serviceTypes, states } from '../data/mock';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    city: '',
    state: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
    setFormData({ name: '', email: '', phone: '', company: '', city: '', state: '', service: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Call Us",
      primary: companyInfo.phone,
      secondary: "Available 24/7"
    },
    {
      icon: Mail,
      title: "Email Us",
      primary: companyInfo.email,
      secondary: "We respond within 24 hours"
    },
    {
      icon: MapPin,
      title: "Locations",
      primary: "600+ Offices Nationwide",
      secondary: "Serving all 50 states"
    },
    {
      icon: Clock,
      title: "Hours",
      primary: "24/7 Operations",
      secondary: "Always available for you"
    }
  ];

  return (
    <>
      <SEO 
        title="Contact Us"
        description="Contact Armed Security Service for professional security guard services. Get a free quote for armed, unarmed, fire watch, and event security services. Available 24/7 nationwide."
        keywords="contact security service, security quote, free security consultation, security guard services contact"
      />
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Contact <span className="text-orange-500">Us</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Get in touch with our team for a free security consultation and quote. 
              We're here to help protect what matters most to you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info) => (
              <div key={info.title} className="bg-slate-800 rounded-xl p-6 text-center hover:bg-slate-750 transition-colors">
                <div className="w-14 h-14 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <info.icon className="w-7 h-7 text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{info.title}</h3>
                <p className="text-orange-400 font-medium">{info.primary}</p>
                <p className="text-gray-500 text-sm">{info.secondary}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column - Info */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Request a Free Security Assessment</h2>
              <p className="text-gray-400 mb-8 leading-relaxed">
                Fill out the form and one of our security experts will contact you within 24 hours 
                to discuss your specific needs. We offer complimentary on-site assessments for 
                businesses and events.
              </p>
              
              <div className="space-y-4 mb-8">
                <h3 className="text-xl font-bold text-white">What to Expect:</h3>
                {[
                  "Free consultation with a security expert",
                  "Customized security plan for your needs",
                  "Competitive pricing with no hidden fees",
                  "Flexible contract options available",
                  "Quick deployment within 24-48 hours"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              {/* Phone CTA */}
              <div className="bg-slate-900 rounded-xl p-6">
                <p className="text-gray-400 mb-3">Need immediate assistance?</p>
                <a 
                  href={`tel:${companyInfo.phone.replace(/[^0-9]/g, '')}`}
                  className="inline-flex items-center gap-3 text-2xl font-bold text-orange-500 hover:text-orange-400 transition-colors"
                >
                  <Phone className="w-8 h-8" />
                  {companyInfo.phone}
                </a>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="bg-slate-900 rounded-2xl p-8">
              {isSubmitted ? (
                <div className="text-center py-16">
                  <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-3">Thank You!</h3>
                  <p className="text-gray-400">Your request has been submitted successfully. Our team will contact you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name *"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address *"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number *"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                    <input
                      type="text"
                      name="company"
                      placeholder="Company Name"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input
                      type="text"
                      name="city"
                      placeholder="City"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                    <input
                      type="text"
                      name="state"
                      placeholder="State"
                      value={formData.state}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none cursor-pointer"
                  >
                    <option value="" disabled>Select Service Type *</option>
                    {serviceTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <textarea
                    name="message"
                    placeholder="Tell us about your security needs..."
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors resize-none"
                  ></textarea>
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-lg font-bold text-lg transition-all hover:shadow-lg"
                  >
                    <Send className="w-5 h-5" />
                    Submit Request
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white">Nationwide Coverage</h2>
            <p className="text-gray-400">We have offices in all 50 states to serve you better</p>
          </div>
          <div className="bg-slate-800 rounded-xl p-8 h-64 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-16 h-16 text-orange-500/50 mx-auto mb-4" />
              <p className="text-gray-400">600+ Office Locations Across the United States</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;